/**
 * LicensePage - page de gestion de la licence côté tenant client.
 * Route /license
 *
 * 3 états :
 *  1. Aucune licence activée  → formulaire saisie clé
 *  2. Licence active           → affichage détaillé (plan, expiration, etc.)
 *  3. Licence expirée          → message + bouton "Saisir une nouvelle clé"
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card, CardHeader, Title2, Title3, Body1, Caption1, Button, Input,
  tokens, MessageBar, MessageBarBody, MessageBarTitle, Badge,
} from '@fluentui/react-components';
import {
  Key24Regular, ShieldCheckmark24Regular, Warning24Regular, ErrorCircle24Regular,
  Delete24Regular, Prohibited24Regular,
} from '@fluentui/react-icons';
import { PageHeader } from '../components/PageHeader';
import { clientLicenseApi, type ClientLicenseStatus } from '../api/licenseApi';
import { useLicense } from '../auth/LicenseGate';

const PLAN_META: Record<string, { label: string; color: string }> = {
  starter:    { label: 'Starter',    color: '#6B7280' },
  pro:        { label: 'Pro',        color: '#1F7A85' },
  business:   { label: 'Business',   color: '#1F3D5C' },
  enterprise: { label: 'Enterprise', color: '#8A2727' },
};

interface LicensePageProps {
  /**
   * Quand true, supprime le `PageHeader` interne pour pouvoir embarquer la
   * section "Licence" dans une autre page (cf. BillingPage qui mount cette
   * section comme premier cadre).
   */
  embedded?: boolean;
}

export function LicensePage({ embedded = false }: LicensePageProps = {}) {
  const { t, i18n } = useTranslation();
  // licenseCtx : context global (lit + refresh). On synchronise notre etat local
  // avec ce context pour eviter le decouplage Sidebar/LicensePage qui faisait
  // qu activer une cle laissait le menu (qui lit le context) en mode trial
  // tant que l user n avait pas rafraichi la page.
  const licenseCtx = useLicense();
  const [status, setStatus]   = useState<ClientLicenseStatus | null>(licenseCtx.status);
  const [loading, setLoading] = useState(licenseCtx.loading);
  const [key, setKey]         = useState('');
  const [activating, setActivating] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Sync local state avec le context (au mount + a chaque refresh du provider)
  useEffect(() => {
    setStatus(licenseCtx.status);
    setLoading(licenseCtx.loading);
  }, [licenseCtx.status, licenseCtx.loading]);

  async function reload() {
    setLoading(true);
    try {
      const s = await clientLicenseApi.status();
      setStatus(s);
      // Notifie le context global pour que Sidebar/AlertsPage/etc. revoient
      // le nouveau statut sans attendre une navigation.
      licenseCtx.refresh();
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  }
  // Pas de fetch au mount : le context LicenseGate l a deja fait. On consomme.

  async function activate(e: React.FormEvent) {
    e.preventDefault();
    setActivating(true);
    setError(null);
    setSuccess(null);
    try {
      const normalized = key.toUpperCase().trim();
      // Validation côté front pour donner un feedback immédiat
      if (!/^NIS-[A-Z2-9]{4}-[A-Z2-9]{4}-[A-Z2-9]{4}-[A-Z2-9]{4}-[A-Z2-9]{4}$/.test(normalized)) {
        throw new Error(`Format invalide. Attendu : NIS-XXXX-XXXX-XXXX-XXXX-XXXX avec uniquement l'alphabet ABCDEFGHJKLMNPQRSTUVWXYZ23456789 (pas de O, I, 0, 1). Reçu : "${normalized}" (${normalized.length} chars).`);
      }
      const s = await clientLicenseApi.activate(normalized);
      setStatus(s);
      // Propage le nouveau statut au context global -> Sidebar deverrouille
      // immediatement les menus Pro/Business sans necessiter un F5.
      licenseCtx.refresh();
      setSuccess(t('license.activate.successBody', { plan: s.plan ?? '', date: s.validUntil ? new Date(s.validUntil).toLocaleDateString(i18n.language) : '' }));
      setKey('');
    } catch (e) {
      const err = e as Error & { status?: number; code?: string };
      const detail = err.code ? ` [code: ${err.code}]` : err.status ? ` [HTTP ${err.status}]` : '';
      setError(`${err.message}${detail}`);
    } finally {
      setActivating(false);
    }
  }

  async function removeLicense() {
    if (!confirm('Retirer la licence active et repasser en mode essai ?\n\nLa clé sera libérée et pourra être réactivée plus tard.')) return;
    setRemoving(true);
    setError(null);
    setSuccess(null);
    try {
      const r = await clientLicenseApi.remove({ releaseLicense: true });
      setSuccess(r.message);
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRemoving(false);
    }
  }

  if (loading) {
    return <div style={{ padding: 32 }}>Chargement…</div>;
  }

  const isRevoked  = status?.hasLicense && status?.revoked;
  const isExpired  = status?.hasLicense && status?.expired && !isRevoked;
  const hasActive  = status?.hasLicense && status?.valid;
  // Permet de saisir une nouvelle cle : pas de licence OU expiree OU revoquee
  const needsActivation = !status?.hasLicense || status?.expired || isRevoked;

  return (
    <div>
      {!embedded && (
        <PageHeader
          title={t('license.title')}
          description={t('license.subtitle')}
        />
      )}

      {error && (
        <MessageBar intent="error" style={{ marginBottom: 16 }}>
          <MessageBarBody>
            <MessageBarTitle>{t('license.activate.errorTitle')}</MessageBarTitle>
            {error}
          </MessageBarBody>
        </MessageBar>
      )}

      {success && (
        <MessageBar intent="success" style={{ marginBottom: 16 }}>
          <MessageBarBody>{success}</MessageBarBody>
        </MessageBar>
      )}

      {/* État LICENCE ACTIVE */}
      {hasActive && status && (
        <Card style={{ marginBottom: 16, borderLeft: `4px solid ${tokens.colorPaletteGreenForeground1}` }}>
          <CardHeader
            image={<ShieldCheckmark24Regular primaryFill={tokens.colorPaletteGreenForeground1}/>}
            header={<Title3 style={{ color: tokens.colorPaletteGreenForeground1 }}>{t('license.active.title')}</Title3>}
            action={
              <Button
                appearance="subtle"
                size="small"
                icon={<Delete24Regular />}
                disabled={removing}
                onClick={removeLicense}
                style={{ color: tokens.colorPaletteRedForeground1 }}
              >
                {removing ? `${t('license.remove.cta')}…` : t('license.remove.cta')}
              </Button>
            }
          />
          <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            <Field label={t('license.active.plan')}>
              <span style={{
                background: PLAN_META[status.plan ?? '']?.color ?? '#999',
                color: '#fff', padding: '4px 12px', borderRadius: 999,
                fontWeight: 700, fontSize: 13,
              }}>
                {PLAN_META[status.plan ?? '']?.label ?? status.plan}
              </span>
            </Field>
            <Field label={t('license.active.validUntil')}>
              <Body1>{status.validUntil && new Date(status.validUntil).toLocaleDateString(i18n.language)}</Body1>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                {status.daysRemaining ? t('trialBanner.daysRemaining', { count: status.daysRemaining }) : ''}
              </Caption1>
            </Field>
            <Field label={t('license.active.activatedAt')}>
              <Body1>{status.activatedAt && new Date(status.activatedAt).toLocaleDateString(i18n.language)}</Body1>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{t('common.by')} {status.activatedBy}</Caption1>
            </Field>
            <Field label={t('license.activate.label')}>
              <code style={{ fontSize: 12, fontFamily: 'Consolas, monospace' }}>{status.key}</code>
            </Field>
            <Field label={t('license.active.customer')}>
              <Body1>{typeof status.customer === 'string' ? status.customer : status.customer?.companyName}</Body1>
              {typeof status.customer === 'object' && status.customer?.email && (
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{status.customer.email}</Caption1>
              )}
            </Field>
          </div>
        </Card>
      )}

      {/* État LICENCE RÉVOQUÉE */}
      {isRevoked && (
        <Card style={{ marginBottom: 16, borderLeft: `4px solid ${tokens.colorPaletteRedForeground1}` }}>
          <CardHeader
            image={<Prohibited24Regular primaryFill={tokens.colorPaletteRedForeground1}/>}
            header={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Title3 style={{ color: tokens.colorPaletteRedForeground1 }}>{t('license.revoked.title')}</Title3>
                <Badge appearance="filled" color="danger">{status?.key}</Badge>
              </div>
            }
            action={
              <Button
                appearance="subtle"
                size="small"
                icon={<Delete24Regular />}
                disabled={removing}
                onClick={removeLicense}
              >
                {t('common.delete')}
              </Button>
            }
          />
          <Body1 style={{ marginTop: 8 }}>
            {t('license.revoked.description', {
              date: status?.revokedAt ? new Date(status.revokedAt).toLocaleDateString(i18n.language) : '—',
            })}
          </Body1>
        </Card>
      )}

      {/* État LICENCE EXPIRÉE */}
      {isExpired && (
        <Card style={{ marginBottom: 16, borderLeft: `4px solid ${tokens.colorPaletteRedForeground1}` }}>
          <CardHeader
            image={<ErrorCircle24Regular primaryFill={tokens.colorPaletteRedForeground1}/>}
            header={<Title3 style={{ color: tokens.colorPaletteRedForeground1 }}>Licence expirée</Title3>}
            action={
              <Button
                appearance="subtle"
                size="small"
                icon={<Delete24Regular />}
                disabled={removing}
                onClick={removeLicense}
              >
                {removing ? 'Nettoyage…' : 'Retirer'}
              </Button>
            }
          />
          <Body1 style={{ marginTop: 8 }}>
            Votre licence a expiré le <strong>{status?.validUntil && new Date(status.validUntil).toLocaleDateString('fr-FR')}</strong>.
            Contactez votre commercial pour la renouveler et saisissez la nouvelle clé ci-dessous.
          </Body1>
        </Card>
      )}

      {/* Formulaire de saisie (état initial OU renouvellement) */}
      {needsActivation && (
        <Card>
          <CardHeader
            image={<Key24Regular primaryFill={tokens.colorBrandForeground1}/>}
            header={<Title2>{t('license.activate.title')}</Title2>}
          />
          <Body1 style={{ marginTop: 8, marginBottom: 16 }}>
            {t('license.noLicense.description')}
          </Body1>
          <form onSubmit={activate}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 360 }}>
                <Caption1 style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>{t('license.activate.label')}</Caption1>
                <Input
                  value={key}
                  onChange={(_, d) => setKey(d.value)}
                  placeholder={t('license.activate.placeholder')}
                  style={{ width: '100%', fontFamily: 'Consolas, monospace', fontSize: 14 }}
                  required
                />
              </div>
              <Button
                appearance="primary"
                type="submit"
                disabled={activating || !key}
                icon={<ShieldCheckmark24Regular/>}
              >
                {activating ? t('license.activate.submitting') : t('license.activate.submit')}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Section info */}
      <Card style={{ marginTop: 16, background: tokens.colorNeutralBackground2 }}>
        <Caption1 style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: tokens.colorNeutralForeground2, textTransform: 'uppercase' }}>
          {t('license.faq.title')}
        </Caption1>
        <details style={{ marginBottom: 8 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{t('license.faq.trialQ')}</summary>
          <Body1 style={{ marginTop: 8 }}>{t('license.faq.trialA')}</Body1>
        </details>
        <details style={{ marginBottom: 8 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{t('license.faq.removeQ')}</summary>
          <Body1 style={{ marginTop: 8 }}>{t('license.faq.removeA')}</Body1>
        </details>
        <details style={{ marginBottom: 8 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{t('license.faq.revokedQ')}</summary>
          <Body1 style={{ marginTop: 8 }}>{t('license.faq.revokedA')}</Body1>
        </details>
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Caption1 style={{ display: 'block', color: tokens.colorNeutralForeground3, fontWeight: 600, textTransform: 'uppercase', fontSize: 11, marginBottom: 4 }}>
        {label}
      </Caption1>
      {children}
    </div>
  );
}
